import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'devextreme-react/button';
import DataGrid, {
  Column,
  Editing,
  Form,
  Lookup,
  Pager,
  Paging,
  Popup,
  RequiredRule,
} from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/form';
import 'devextreme-react/text-area';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import CustomStore from 'devextreme/data/custom_store';
import $createDataSource from 'utils/create-data-souce-instance';
import { requestHelper } from 'utils/request-helper';

import TabPanelComponent from './components/Tabs';

type NomenclatureCompositionPageProps = {
  initialId: number | null;
};

const NomenclatureCompositionPage: FC<NomenclatureCompositionPageProps> = ({ initialId }) => {
  const [activeTab, setActiveTab] = useState<number>(1);
  const [materials, setMaterials] = useState<any[]>([]);

  const unitData: any = useCallback(async () => {
    const data = await $createDataSource('/v1/material', 'id').load();
    setMaterials(data);
  }, []);

  useEffect(() => {
    unitData();
  }, [unitData]);

  const NomenclatureDataStore: any = useMemo(() => {
    const defaultFilter = ['factoryId', activeTab];

    return new CustomStore({
      key: 'id',
      load: (loadOptions) =>
        requestHelper(`/v1/NomenclatureComposition/${initialId}`, 'get', { ...loadOptions, filter: defaultFilter }),
      insert: async (values) => {
        await requestHelper(`/v1/NomenclatureComposition/${initialId}`, 'post', {
          values: { ...values, factoryId: activeTab },
        });
      },

      remove: async (key) =>
        await requestHelper(`/v1/NomenclatureComposition/${key}`, 'delete', {
          key: key,
        }),

      update: async (key, values) =>
        await requestHelper(`/v1/NomenclatureComposition/${key}`, 'put', {
          key: key,
          values: { ...values, factoryId: activeTab },
        }),
    });
  }, [activeTab, initialId]);

  const refGrid = useRef<DataGrid | null>(null);
  const addRow = useCallback(() => refGrid.current?.instance.addRow(), []);

  return (
    <div>
      <AppLayoutTop title="Спецификации">
        <Button width={120} text="Создать" type="default" onClick={addRow} />
      </AppLayoutTop>

      <TabPanelComponent setActiveTab={setActiveTab} activeTab={activeTab} />

      <AppCard>
        <DataGrid
          dataSource={NomenclatureDataStore}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          remoteOperations={true}
          onRowUpdating={(e) => (e.newData = { ...e.oldData, ...e.newData })}
          onInitNewRow={(e) => (e.data = { materialId: null, unitName: '', quantity: undefined })}
          searchPanel={{
            visible: false,
          }}
        >
          <Editing
            mode="popup"
            allowUpdating={true}
            texts={{
              editRow: 'Просмотр',
            }}
            allowDeleting={true}
          >
            <Popup title="Просмотр информации" showTitle={true} width={400} height="auto" />
            <Form>
              <Item itemType="group" colCount={2} colSpan={2}>
                <Item dataField="materialId" caption="Материал" colSpan={2}>
                  <RequiredRule message="Заполните поле" />
                </Item>

                <Item dataField="quantity" caption="Объём" colSpan={2} editorType="dxNumberBox">
                  <RequiredRule message="Заполните поле Объём" />
                </Item>
                <Item dataField="comment" caption="Комментарий" colSpan={2} editorType="dxTextArea"></Item>
                {/* <Item dataField="isDeleted" caption="Удалён" colSpan={2} /> */}
              </Item>
            </Form>
          </Editing>

          <Column dataField="materialId" caption="Материал" width={200}>
            <Lookup dataSource={materials} valueExpr="id" displayExpr="name" />
          </Column>

          <Column dataField="quantity" caption="Объём" />
          {/* <Column dataField="isDeleted" caption="Удалён" width={100} /> */}
          <Column dataField="comment" caption="Комментарий" width={100} visible={false} />

          <Paging defaultPageSize={15} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default NomenclatureCompositionPage;
